import { useState } from 'react';
import type {
  CustomerManagerPreciseRunwayCustomer,
  CustomerManagerUsageDailyResponse,
} from '@nadoutong/contracts';
import { Download } from 'lucide-react';
import { crmUsageDailyCsvUrl, useCrmUsageDaily } from '../../api/opsUsage.ts';
import type { CrmUsageDailyQuery } from '../../api/opsUsage.ts';
import { useCustomerManagerCustomerRunway } from '../../api/customerManagerWorkspace.ts';
import { usePricing } from '../../api/pricing.ts';
import { LocalDateTimeInput } from '../../components/LocalDateTimeInput.tsx';
import {
  formatDisplayRateHint,
  formatUtc8DateTimeInputValue,
  formatUtc8Timestamp,
  formatCompact,
  formatUsdMicrosOrCents,
  formatUsdMicrosOrCentsAsCnyDisplay,
  utc8DateTimeInputValueToIso,
} from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, EmptyState, KpiCard, buttonClass } from '../../ui/index.ts';
import { UsageDailyTable } from './UsageDailyTable.tsx';
import { FIXED_RUNWAY_BURN_DAYS, UsageRunwayPanel } from './UsageRunwayPanel.tsx';
import type {
  RunwayBurnBaseline,
  RunwayFixedSample,
} from './UsageRunwayPanel.tsx';
import { UsageSharePanel } from './UsageSharePanel.tsx';
import { UsageTrendPanel } from './UsageTrendPanel.tsx';
import {
  alignPrevious,
  cacheHitRate,
  deltaPercent,
  formatDelta,
  isEmptyUsage,
  sumThrough,
} from './usageMetrics.ts';
import {
  USAGE_TAB_WINDOWS,
  elapsedWindowDays,
  nowAtCoordinate,
  occurredThroughIndex,
  resolveUsageTabQuery,
  utc8DateString,
  utc8DayFraction,
  utc8HourFraction,
  utc8HourKey,
  type UsageTabWindow,
} from './usageWindows.ts';

const WINDOW_KEYS: Record<UsageTabWindow, I18nKey> = {
  today: 'customer_manager_360.usage.window.today',
  week: 'customer_manager_360.usage.window.week',
  month: 'customer_manager_360.usage.window.month',
  '7d': 'customer_manager_360.usage.window.7d',
  '30d': 'customer_manager_360.usage.window.30d',
  custom: 'customer_manager_360.usage.window.custom',
};

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');

export function UsageTab({
  accountId,
}: {
  readonly accountId: string;
}): JSX.Element {
  const t = useT();
  const [pill, setPill] = useState<UsageTabWindow>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [burn, setBurn] = useState<RunwayBurnBaseline>('7');
  const nowMs = Date.now();
  const resolved = resolveUsageTabQuery(pill, customFrom, customTo, nowMs);
  const dailyEnabled =
    resolved.window !== 'custom' || (resolved.from !== undefined && resolved.to !== undefined);
  const dailyQuery: CrmUsageDailyQuery = {
    window: resolved.window,
    from: resolved.from,
    to: resolved.to,
    accountId,
    granularity: resolved.granularity,
  };
  const daily = useCrmUsageDaily(dailyQuery, dailyEnabled);
  const runway1 = useCustomerManagerCustomerRunway(accountId, 1, true);
  const runway3 = useCustomerManagerCustomerRunway(accountId, 3, true);
  const runway7 = useCustomerManagerCustomerRunway(accountId, 7, true);
  const runway30 = useCustomerManagerCustomerRunway(accountId, 30, true);
  const fixedRunways = {
    1: runway1,
    3: runway3,
    7: runway7,
    30: runway30,
  } as const;
  const selectedFixedDays: (typeof FIXED_RUNWAY_BURN_DAYS)[number] =
    burn === '1' ? 1 : burn === '3' ? 3 : burn === '30' ? 30 : 7;
  const runwayCustomer = fixedRunways[selectedFixedDays].data?.customers[0] ?? null;
  const fixedDaysLeft = FIXED_RUNWAY_BURN_DAYS.map((days) =>
    runwaySampleFromQuery(fixedRunways[days]),
  );
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const today = utc8DateString(nowMs);
  const nowBucket = pill === 'today' ? utc8HourKey(nowMs) : today;
  const dayFraction = utc8DayFraction(nowMs);
  const nowFraction = pill === 'today' ? utc8HourFraction(nowMs) : dayFraction;
  const interpolatePrevious = pill === 'today' || pill === 'week' || pill === 'month';
  const occurredIndex =
    daily.data === undefined
      ? -1
      : occurredThroughIndex(
          daily.data.days.map((day) => day.date),
          nowBucket,
        );
  const elapsedDays =
    daily.data === undefined
      ? 1e-9
      : elapsedWindowDays(pill, daily.data.window, nowMs, occurredIndex, dayFraction);
  const currentSpend =
    daily.data === undefined
      ? 0
      : sumThrough(
          daily.data.days.map((day) => day.usd_charged_micros),
          occurredIndex,
          1,
        );
  const nowAt =
    daily.data === undefined
      ? undefined
      : nowAtCoordinate(
          daily.data.days.map((day) => day.date),
          occurredIndex,
          nowFraction,
          nowBucket,
        );

  return (
    <div className="flex flex-col gap-3.5" data-testid="customer-360-usage">
      <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label={t('customer_manager_360.usage.window_aria')}>
        {USAGE_TAB_WINDOWS.map((window) => {
          const active = window === pill;
          return (
            <Button
              key={window}
              role="tab"
              aria-selected={active}
              variant="secondary"
              size="sm"
              className={active ? 'bg-signal-dim text-signal-bright' : undefined}
              data-testid={`customer-360-usage-window-${window}`}
              onClick={() => {
                setPill(window);
              }}
            >
              {t(WINDOW_KEYS[window])}
            </Button>
          );
        })}
        {daily.data !== undefined ? (
          <span
            className="rounded border border-line-soft bg-well px-2.5 py-1 font-mono text-micro text-paper-low"
            data-testid="customer-360-usage-window-range"
          >
            {formatUtc8Timestamp(daily.data.window.from)} — {formatUtc8Timestamp(daily.data.window.to)}
          </span>
        ) : null}
        <span className="text-micro text-paper-low">{t('customer_manager_360.usage.tz_hint')}</span>
        <span className="ml-auto">
          <a
            href={crmUsageDailyCsvUrl(dailyQuery)}
            download="crm-usage-daily.csv"
            data-testid="customer-360-usage-csv"
            className={buttonClass('secondary', 'sm')}
          >
            <Download aria-hidden="true" className="h-3.5 w-3.5" />
            {t('customer_manager_360.usage.export_csv')}
          </a>
        </span>
      </div>
      {pill === 'custom' ? (
        <div className="flex flex-wrap items-end gap-2">
          <LocalDateTimeInput
            label={t('customer_manager_360.usage.from_utc8')}
            value={customFrom}
            onChange={setCustomFrom}
            formatValue={formatUtc8DateTimeInputValue}
            parseValue={utc8DateTimeInputValueToIso}
            inputClassName="w-56"
            testId="customer-360-usage-from"
          />
          <LocalDateTimeInput
            label={t('customer_manager_360.usage.to_utc8')}
            value={customTo}
            onChange={setCustomTo}
            formatValue={formatUtc8DateTimeInputValue}
            parseValue={utc8DateTimeInputValueToIso}
            inputClassName="w-56"
            testId="customer-360-usage-to"
          />
        </div>
      ) : null}

      {daily.isPending ? (
        <div className="rounded-lg border border-line-soft bg-card p-6 text-body text-paper-low" data-testid="customer-360-usage-loading">
          {t('customer_manager_360.usage.loading')}
        </div>
      ) : null}
      {daily.isError ? (
        <div
          role="alert"
          className="rounded-lg border border-bad-line bg-bad-dim/50 p-6 text-body text-bad-bright"
          data-testid="customer-360-usage-error"
        >
          {t('customer_manager_360.usage.error', {
            message: daily.error instanceof Error ? daily.error.message : 'unknown error',
          })}
        </div>
      ) : null}

      {daily.data === undefined || isEmptyUsage(daily.data.totals) ? (
        <UsageRunwayPanel
          customer={runwayCustomer}
          burn={burn}
          onBurnChange={setBurn}
          fixedDaysLeft={fixedDaysLeft}
          cnyPerUsd={cnyPerUsd}
          nowMs={nowMs}
        />
      ) : null}

      {daily.data !== undefined && isEmptyUsage(daily.data.totals) ? (
        <EmptyState
          testId="customer-360-usage-empty"
          title={t('customer_manager_360.usage.empty.title')}
          hint={t('customer_manager_360.usage.empty.hint')}
        />
      ) : null}

      {daily.data !== undefined && !isEmptyUsage(daily.data.totals) ? (
        <UsageReady
          data={daily.data}
          pill={pill}
          calendar={resolved.calendar}
          interpolatePrevious={interpolatePrevious}
          today={nowBucket}
          nowFraction={nowFraction}
          occurredIndex={occurredIndex}
          elapsedDays={elapsedDays}
          nowAt={nowAt}
          currentSpend={currentSpend}
          cnyPerUsd={cnyPerUsd}
          burn={burn}
          onBurnChange={setBurn}
          fixedDaysLeft={fixedDaysLeft}
          runwayCustomer={runwayCustomer}
          nowMs={nowMs}
        />
      ) : null}
    </div>
  );
}

function UsageReady({
  data,
  pill,
  calendar,
  interpolatePrevious,
  today,
  nowFraction,
  occurredIndex,
  elapsedDays,
  nowAt,
  currentSpend,
  cnyPerUsd,
  burn,
  onBurnChange,
  fixedDaysLeft,
  runwayCustomer,
  nowMs,
}: {
  readonly data: CustomerManagerUsageDailyResponse;
  readonly pill: UsageTabWindow;
  readonly calendar: boolean;
  readonly interpolatePrevious: boolean;
  readonly today: string;
  readonly nowFraction: number;
  readonly occurredIndex: number;
  readonly elapsedDays: number;
  readonly nowAt: number | undefined;
  readonly currentSpend: number;
  readonly cnyPerUsd: number | null;
  readonly burn: RunwayBurnBaseline;
  readonly onBurnChange: (next: RunwayBurnBaseline) => void;
  readonly fixedDaysLeft: readonly RunwayFixedSample[];
  readonly runwayCustomer: CustomerManagerPreciseRunwayCustomer | null;
  readonly nowMs: number;
}): JSX.Element {
  const t = useT();
  const fraction = interpolatePrevious ? nowFraction : 1;
  const previousDays = alignPrevious(data.days, data.previous.days);
  const previousSpendToNow = sumThrough(
    previousDays.map((day) => day.usd_charged_micros),
    occurredIndex,
    fraction,
  );
  const spendDelta = deltaPercent(currentSpend, previousSpendToNow);
  const currentReqs = sumThrough(
    data.days.map((day) => day.request_count),
    occurredIndex,
    1,
  );
  const previousReqsToNow = sumThrough(
    previousDays.map((day) => day.request_count),
    occurredIndex,
    fraction,
  );
  const previousFullSpend = data.previous.totals.usd_charged_micros;
  const successRate =
    data.totals.request_count > 0 ? (data.totals.success_count / data.totals.request_count) * 100 : 0;
  const cacheHit = cacheHitRate(data.totals);
  const cacheHitPct = cacheHit === null ? '—' : `${cacheHit.toFixed(1)}%`;
  const baselineKey = calendar
    ? 'customer_manager_360.usage.compare.at_point'
    : 'customer_manager_360.usage.compare.same_span';
  const avgLabel = pill === 'today' ? t('customer_manager_360.usage.kpi.avg_hour') : t('customer_manager_360.usage.kpi.avg');
  const avgSpend = pill === 'today'
    ? currentSpend / Math.max(occurredIndex + nowFraction, 1e-9)
    : currentSpend / elapsedDays;

  return (
    <>
      <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-5">
        <KpiCard
          label={t('customer_manager_360.usage.kpi.spend', { window: t(WINDOW_KEYS[pill]) })}
          value={formatUsdMicrosOrCentsAsCnyDisplay(currentSpend, Math.round(currentSpend / 10_000), cnyPerUsd, false)}
          delta={{
            text: t('customer_manager_360.usage.kpi.spend_sub', {
              usd: formatUsdMicrosOrCents(currentSpend, Math.round(currentSpend / 10_000), false),
              rate: formatDisplayRateHint(cnyPerUsd),
              delta: spendDelta === null ? '' : ` ${formatDelta(spendDelta)}`,
              baseline: t(baselineKey),
            }),
            tone: 'neutral',
          }}
          testId="customer-360-usage-kpi-spend"
        />
        <KpiCard
          label={avgLabel}
          value={formatUsdMicrosOrCentsAsCnyDisplay(avgSpend, Math.round(avgSpend / 10_000), cnyPerUsd, false)}
          delta={{
            text: t('customer_manager_360.usage.kpi.avg_sub', {
              amount: formatUsdMicrosOrCentsAsCnyDisplay(
                previousFullSpend,
                Math.round(previousFullSpend / 10_000),
                cnyPerUsd,
                false,
              ),
            }),
            tone: 'neutral',
          }}
          testId="customer-360-usage-kpi-avg"
        />
        <KpiCard
          label={t('customer_manager_360.usage.kpi.requests')}
          value={TOKEN_FORMATTER.format(Math.round(currentReqs))}
          delta={{
            text: t('customer_manager_360.usage.kpi.requests_sub', {
              baseline: t(baselineKey),
              amount: TOKEN_FORMATTER.format(Math.round(previousReqsToNow)),
            }),
            tone: 'neutral',
          }}
          testId="customer-360-usage-kpi-requests"
        />
        <KpiCard
          label={t('customer_manager_360.usage.kpi.success')}
          value={`${successRate.toFixed(1)}%`}
          tone="ok"
          delta={{
            text: t('customer_manager_360.usage.kpi.success_sub', {
              error: TOKEN_FORMATTER.format(data.totals.error_count),
              limited: TOKEN_FORMATTER.format(data.totals.rate_limited_count),
            }),
            tone: 'neutral',
          }}
          testId="customer-360-usage-kpi-success"
        />
        <KpiCard
          label={t('customer_manager_360.usage.kpi.tokens')}
          value={`${formatCompact(data.totals.input_tokens)} / ${formatCompact(data.totals.output_tokens)}`}
          delta={{
            text: t('customer_manager_360.usage.kpi.tokens_sub', {
              pct: cacheHitPct,
            }),
            tone: 'neutral',
          }}
          testId="customer-360-usage-kpi-tokens"
        />
      </div>

      <UsageRunwayPanel
        customer={runwayCustomer}
        burn={burn}
        onBurnChange={onBurnChange}
        fixedDaysLeft={fixedDaysLeft}
        cnyPerUsd={cnyPerUsd}
        nowMs={nowMs}
      />

      <UsageTrendPanel
        data={data}
        pill={pill}
        calendar={calendar}
        interpolatePrevious={interpolatePrevious}
        occurredIndex={occurredIndex}
        dayFraction={nowFraction}
        nowAt={nowAt}
        cnyPerUsd={cnyPerUsd}
      />

      <div className="grid gap-3.5 xl:grid-cols-2">
        <UsageSharePanel kind="model" rows={data.by_day_model} cnyPerUsd={cnyPerUsd} />
        <UsageSharePanel kind="sku" rows={data.by_day_model} cnyPerUsd={cnyPerUsd} />
      </div>

      <UsageDailyTable data={data} today={today} cnyPerUsd={cnyPerUsd} />
    </>
  );
}

function runwaySampleFromQuery(query: {
  readonly isError: boolean;
  readonly data:
    | { readonly customers: readonly { readonly days_left: number | null }[] }
    | undefined;
}): RunwayFixedSample {
  if (query.isError) return { kind: 'error' };
  if (query.data === undefined) return { kind: 'pending' };
  return { kind: 'value', daysLeft: query.data.customers[0]?.days_left ?? null };
}
